import React, { useRef, useState, useEffect } from 'react'
import {
  View,
  StyleSheet,
  Image,
  TouchableOpacity,
  ActivityIndicator,
  Keyboard,
  LayoutChangeEvent,
  Modal,
  Platform,
} from 'react-native'
import {
  UNSTABLE_usePreventRemove as usePreventRemove,
  useNavigation,
  useRoute,
  useTheme,
} from '@react-navigation/native'
import { onlineManager } from '@tanstack/react-query'
import { v4 as uuid } from 'uuid'
import {
  Text,
  Icon,
  TextInput,
  DeleteModal,
  TranslateLangPicker,
  BottomModalBase,
  KeyboardAwareView,
} from '@components'
import { Theme } from '@theme'
import {
  getCommonStyles,
  isPlanLimitError,
  Scope,
  showApiErrorToast,
  showErrorToast,
  showInfoToast,
} from '@utils'
import dayjs from 'dayjs'
import {
  useCreateNote,
  useCreationStackIntegration,
  useDeleteNote,
  useFtTranslateText,
  useFtVoiceTranscribe,
  useGetContactById,
  useGetNoteById,
  useKeyboardOffset,
  useScreen,
  useUpdateNote,
  type Contact,
  type TranslateTextLanguage,
} from '@hooks'
import { useVoiceRecorder } from '../../../hooks/useVoiceRecorder'
import { NoteAbout } from './components/NoteAbout'
import { NotePersonCompact } from './components/NotePersonCompact'
import { NotePersonPicker } from './components/NotePersonPicker'
import { SafeAreaView } from 'react-native-safe-area-context'
import { defaultImage } from '@assets'
import { useCurrentUserStore } from '@store'
import { useLanguage } from '@context'

interface EditNoteProps {
  noteId?: string
  isShared?: boolean
  contactId?: string
  // True when the note is being authored on a ghost (non-onboarded) contact.
  // Ghost profiles have no real account to share with, so the shared toggle
  // is hidden and the save payload is forced to isShared=false.
  isGhost?: boolean
  isCreationStack?: boolean
  // The notes of a group conversation: their « person » is the group, which has
  // no profile to show, nothing to change and nobody to share with: the note
  // is always personal.
  hidePerson?: boolean
  onComplete?: () => void
  onBack?: () => void
}

// The note saves itself this long after the last keystroke.
const AUTOSAVE_DELAY_MS = 800
// After a save that failed for want of a connection, the next tries on their
// own: after 5 s, 10 s, 20 s, then every 30 s (an editor left open while the
// server is down must not ask it every 5 s for hours).
const RETRY_DELAY_MS = 5000
const RETRY_DELAY_MAX_MS = 30000

// What a failed save calls for:
// - « stop »: the free notes are used up (the paywall shows) or the note no
//   longer exists; saving stops for good;
// - « later »: no connection, a server hiccup; tried again a moment later;
// - « change »: the server refuses this very content; tried again once
//   something changes in the note.
const statusOf = (error: unknown) =>
  (error as { response?: { status?: number } } | undefined)?.response?.status
const failureKind = (error: unknown): 'stop' | 'later' | 'change' => {
  if (isPlanLimitError(error)) return 'stop'
  const status = statusOf(error)
  if (status === 404) return 'stop'
  if (!status || status >= 500 || status === 401 || status === 408 || status === 429) return 'later'
  return 'change'
}
// The API refuses to share a note with someone who is not a contact on both
// sides (a person added from an event who has not added back).
const isShareRefusal = (error: unknown) =>
  (error as { response?: { data?: { code?: string } } } | undefined)?.response?.data?.code ===
  'Networking.Note.CanOnlyShareWithContacts'
// How long after the keyboard's field loses focus the phone leaves its writing
// mode (going from the title to the text must not count as leaving).
const WRITING_END_DELAY_MS = 150
// Time for one bottom sheet to slide away before the next one opens.
const SHEET_SWAP_DELAY_MS = 400
// The list of profiles leaves the top of the note visible behind it.
const CHOOSER_HEIGHT_RATIO = 0.75
// On the computer the text and the panel on its right (« À propos de ») need
// this much width together. In a narrower editor (a small window, the menu
// open) the text was left with a few pixels and the title spilled over the
// panel: below it, the editor takes the one-column layout of the side panel.
const TWO_COLUMN_MIN_WIDTH = 660
// The width of the last editor shown: the next one (a fresh editor opens for
// every note) starts in the right layout instead of switching after a frame.
let lastEditorWidth = 0

type NoteDraft = {
  title: string
  content: string
  contactId: string | null
  isShared: boolean
  isPinned: boolean
}

// What the phone sends to leave a screen: the Android back button (GO_BACK)
// and the iPhone back swipe (POP).
const SYSTEM_BACK_ACTIONS = ['GO_BACK', 'POP', 'POP_TO_TOP']

// Holds the editor's screen in place while a note waits to be saved, and tells
// when the phone tried to leave it (Android back button, iPhone back swipe).
// A component of its own: it needs the route of the screen, which the editor
// does not have when it is shown inside another screen.
const NoteRemovalGuard = ({
  isActive,
  onPrevented,
}: {
  isActive: boolean
  onPrevented: Parameters<typeof usePreventRemove>[1]
}) => {
  usePreventRemove(isActive, onPrevented)
  return null
}

const formatDuration = (seconds: number) =>
  `${Math.floor(seconds / 60)}:${(seconds % 60).toString().padStart(2, '0')}`

export const EditNote: React.FC<EditNoteProps> = ({
  noteId: propNoteId,
  contactId: propContactId,
  isShared: isSharedProps,
  isGhost: isGhostProps,
  isCreationStack = false,
  hidePerson = false,
  onComplete,
  onBack,
}) => {
  const theme = useTheme()
  const { t } = useLanguage()
  const { isMobile, isWeb } = useScreen()
  const styles = getStyle(theme)
  const commonStyles = getCommonStyles(theme)
  const navigation = useNavigation()
  // Use prop noteId or try to get from route params
  let routeNoteId: string | undefined
  let routeContactId: string | undefined
  let routeIsGhost: boolean | undefined
  try {
    const route = useRoute<any>()
    routeNoteId = route.params?.noteId
    routeContactId = route.params?.contactId
    // After a page reload on the web the param comes back as the text « false ».
    const rawIsGhost: unknown = route.params?.isGhost
    routeIsGhost =
      rawIsGhost === undefined ? undefined : rawIsGhost === true || rawIsGhost === 'true'
  } catch (error) {
    routeNoteId = undefined
  }

  const { currentStack, isVisible } = useCreationStackIntegration()
  const noteId = propNoteId || routeNoteId || currentStack?.data?.noteId
  // What the caller says about the person the editor opens on. Once another
  // person is chosen here, only that person's own profile tells.
  const openedOnGhost = !!(isGhostProps ?? routeIsGhost ?? currentStack?.data?.isGhost ?? false)
  const [personChanged, setPersonChanged] = useState(false)
  // A ghost profile picked here is known at once, from its row in the list
  // (its own profile takes a moment to load).
  const [pickedGhostId, setPickedGhostId] = useState<string | null>(null)

  // Silent: the header says « Enregistré », a toast on every save would be noise.
  const { mutateAsync: createNote } = useCreateNote({ silent: true })
  const { mutateAsync: updateNote } = useUpdateNote({ silent: true })
  const { mutate: deleteNote } = useDeleteNote()

  const { userID } = useCurrentUserStore()
  const { data: note } = useGetNoteById(noteId)
  const isMyNote = !noteId || note?.createdByAccountId === userID
  // A note someone shared with me opens read-only: only its author edits or
  // deletes it (the API refuses it too).
  const isReadOnly = !!noteId && !!note && !isMyNote
  // An existing note cannot be typed in before it is there: what was written
  // in the still empty editor was replaced when the note arrived.
  const isLoadingNote = !!noteId && !note

  const [title, setTitle] = useState(note?.title || '')
  const [content, setContent] = useState(note?.content || '')
  const [contactId, setContactId] = useState<string | null>(
    (isMyNote ? note?.contactAccountId : note?.createdByAccountId) ||
      routeContactId ||
      currentStack?.data?.contactId ||
      propContactId,
  )
  const isGhost = personChanged ? !!contactId && contactId === pickedGhostId : openedOnGhost
  const [isShared, setIsShared] = useState<boolean>(
    isGhost || hidePerson ? false : note?.isShared || isSharedProps || false,
  )
  const [isPinned, setIsPinned] = useState<boolean>(!!note?.isPinned)
  const [isChooserOpen, setIsChooserOpen] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const { data: contact } = useGetContactById(contactId || '')
  // Someone added from an event who has not added back (told by the API, or
  // learnt from its refusal): a note cannot be shared with them yet.
  const [refusedShareContactId, setRefusedShareContactId] = useState<string | null>(null)
  const refusedShareContactIdRef = useRef<string | null>(null)
  const contactNotAddedBack =
    !!contactId && (!!contact?.oneWay || contactId === refusedShareContactId)

  // Translate state — driven by the icon in the header. Cache per language
  // so toggling between FR/EN/ES is free after the first call.
  const translateIconRef = useRef<View>(null)
  const [showLangPicker, setShowLangPicker] = useState(false)
  const [pickerPos, setPickerPos] = useState<{ x: number; y: number } | undefined>()
  const [translatedTo, setTranslatedTo] = useState<TranslateTextLanguage | null>(null)
  const [translationCache, setTranslationCache] = useState<
    Partial<Record<TranslateTextLanguage, { title: string; content: string }>>
  >({})
  const { mutateAsync: translateText, isPending: isTranslating } = useFtTranslateText()

  const onPickLang = (lang: TranslateTextLanguage) => {
    if (lang === translatedTo) {
      setTranslatedTo(null)
      setShowLangPicker(false)
      return
    }
    if (translationCache[lang]) {
      setTranslatedTo(lang)
      setShowLangPicker(false)
      return
    }
    if (!content.trim() && !title.trim()) return

    // Translate title and content separately, in parallel. Doing them in
    // one call with a separator was unreliable — Gemini sometimes
    // dropped the marker and merged the two fields.
    Promise.all([
      title.trim()
        ? translateText({ text: title, targetLanguage: lang })
        : Promise.resolve({ translatedText: title }),
      content.trim()
        ? translateText({ text: content, targetLanguage: lang })
        : Promise.resolve({ translatedText: content }),
    ])
      .then(([titleRes, contentRes]) => {
        setTranslationCache((prev) => ({
          ...prev,
          [lang]: {
            title: titleRes.translatedText,
            content: contentRes.translatedText,
          },
        }))
        setTranslatedTo(lang)
        setShowLangPicker(false)
      })
      .catch((err) => {
        showApiErrorToast(t, err)
      })
  }

  const displayedTitle = translatedTo ? (translationCache[translatedTo]?.title ?? title) : title
  const displayedContent = translatedTo
    ? (translationCache[translatedTo]?.content ?? content)
    : content
  const isTranslated = translatedTo !== null

  // ----- Automatic saving -------------------------------------------------
  // The note saves itself while you write: created at the first words, then
  // updated. Saves run one after the other, and closing the editor (or
  // leaving the screen) saves what is left: nothing is lost.
  const canEdit = isMyNote && !isReadOnly
  const draft: NoteDraft = {
    title,
    content,
    contactId: contactId ?? null,
    isShared: !!contactId && !isGhost && !hidePerson && isShared,
    isPinned,
  }
  const draftKey = JSON.stringify(draft)
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'failed'>('idle')
  const [savedNoteId, setSavedNoteId] = useState<string | undefined>(noteId)
  const idRef = useRef<string | undefined>(noteId)
  // A new note opened as « shared » (the « Partagé » tab of that person) goes
  // back to « Moi seul » instead of being refused at its first save.
  useEffect(() => {
    if (contactNotAddedBack && isShared && !idRef.current) setIsShared(false)
  }, [contactNotAddedBack, isShared])
  const lastSavedKeyRef = useRef<string | null>(null)
  // True while a save of this editor is under way: the stored note is already
  // ahead of `lastSavedKeyRef`, and must not be taken for someone else's change.
  const isSavingRef = useRef(false)
  const saveChainRef = useRef<Promise<void>>(Promise.resolve())
  // Set once the free notes are used up (the paywall is shown), the note no
  // longer exists or it was deleted here: no more saving on its own.
  const stopSavingRef = useRef(false)
  // The draft whose save failed, kept to be saved: `null` when nothing failed.
  const failedKeyRef = useRef<string | null>(null)
  // Whether that failed save is tried again by itself (no connection) or waits
  // for a change in the note (the server refused it).
  const retryLaterRef = useRef(false)
  // How many times in a row the save of that same draft failed: each try on
  // its own waits longer than the one before.
  const retryCountRef = useRef(0)
  // The id of a note still to be created, chosen here once: a create sent again
  // (its answer was lost on a bad connection) then brings the note made by the
  // first request up to date, instead of making a second one.
  const newNoteIdRef = useRef<string | null>(null)
  const scopeRef = useRef<string | undefined>(contact?.scope)
  scopeRef.current = contact?.scope
  // Privé or Pro, from the person picked in this editor: a note written before
  // its person was chosen is created as « Privé », and must follow a Pro contact.
  const pickedScopeRef = useRef<Scope | undefined>(undefined)

  const persist = (draftToSave: NoteDraft, { isLeaving = false } = {}) => {
    const save = async (snapshot: NoteDraft): Promise<void> => {
      const key = JSON.stringify(snapshot)
      if (stopSavingRef.current) return
      const isSaved = key === lastSavedKeyRef.current
      if (isSaved || !snapshot.content.trim()) {
        // Nothing left to save (back to the saved note, or no text: the API
        // needs one): a save that failed earlier no longer stands. « Non
        // enregistré » and the question on closing used to stay for good.
        if (failedKeyRef.current !== null) {
          failedKeyRef.current = null
          setSaveStatus(isSaved ? 'saved' : 'idle')
        }
        return
      }
      setSaveStatus('saving')
      isSavingRef.current = true
      let wasSent = false
      try {
        // On the web, a save started while the browser is offline is kept back
        // until the connection returns and gives no answer meanwhile: the
        // header stayed on « Enregistrement… » and the back arrow did nothing.
        // It is counted as failed at once. Only the save made on leaving the
        // screen is still handed over, to be sent when the connection returns.
        if (!isLeaving && !onlineManager.isOnline()) throw new Error('offline')
        wasSent = true
        if (idRef.current) {
          await updateNote({
            id: idRef.current,
            title: snapshot.title,
            content: snapshot.content,
            contactAccountId: snapshot.contactId,
            scope: pickedScopeRef.current,
            isShared: snapshot.isShared,
            isPinned: snapshot.isPinned,
          })
        } else {
          newNoteIdRef.current ??= uuid()
          const id = await createNote({
            id: newNoteIdRef.current,
            title: snapshot.title,
            content: snapshot.content,
            contactAccountId: snapshot.contactId,
            scope: pickedScopeRef.current || scopeRef.current || Scope.PRIVATE,
            isShared: snapshot.isShared,
            isPinned: snapshot.isPinned,
          })
          idRef.current = id
          setSavedNoteId(id)
        }
        lastSavedKeyRef.current = key
        failedKeyRef.current = null
        retryCountRef.current = 0
        setSaveStatus('saved')
      } catch (error) {
        const kind = failureKind(error)
        if (
          isShareRefusal(error) &&
          snapshot.isShared &&
          refusedShareContactIdRef.current !== snapshot.contactId
        ) {
          // The person has not added back: the note is saved at once as a
          // personal one, with one sentence saying so. It used to stay « Non
          // enregistré » until the choice was put back by hand. Once per
          // person: a second refusal is shown like any other.
          refusedShareContactIdRef.current = snapshot.contactId
          setRefusedShareContactId(snapshot.contactId)
          setIsShared(false)
          draftRef.current = { ...draftRef.current, isShared: false }
          showInfoToast(t('notesForm.onlyMeSubtitle'), t('notesForm.savedForMeOnly'))
          setSaveStatus('idle')
          await save({ ...snapshot, isShared: false })
          return
        }
        if (kind === 'stop') {
          // A shared note that cannot be made personal, the free notes being
          // used up: the choice goes back to shared and the note keeps saving.
          // It used to save nothing more until it was opened again.
          const saved = lastSavedKeyRef.current
            ? (JSON.parse(lastSavedKeyRef.current) as NoteDraft)
            : null
          const isRefusedUnshare =
            isPlanLimitError(error) &&
            !!idRef.current &&
            !snapshot.isShared &&
            !!saved?.isShared &&
            saved.contactId === snapshot.contactId
          if (isRefusedUnshare) {
            setIsShared(true)
            // What was typed along with that choice is saved at once, as
            // shared: the editor may close before it is drawn again, and the
            // save made on leaving would send the refused choice once more.
            draftRef.current = { ...draftRef.current, isShared: true }
            setSaveStatus('idle')
            await save({ ...snapshot, isShared: true })
            return
          }
          // The paywall is already on screen for a used-up quota.
          stopSavingRef.current = true
          failedKeyRef.current = null
          if (!isPlanLimitError(error)) showApiErrorToast(t, error)
          setSaveStatus('idle')
        } else {
          // The note stays to be saved: at the next change, on closing, and by
          // itself a moment later when the connection was the trouble. One
          // save that failed used to stop every later one, closing included.
          // Said once, not at every try; the header keeps « Non enregistré ».
          if (failedKeyRef.current === null) {
            // No answer at all: the connection, said in plain words.
            if (statusOf(error)) showApiErrorToast(t, error)
            else showErrorToast(t('editNote.notSavedTitle'), t('editNote.notSavedMessage'))
          }
          retryCountRef.current = failedKeyRef.current === key ? retryCountRef.current + 1 : 0
          failedKeyRef.current = key
          retryLaterRef.current = kind === 'later'
          // Sent, and no answer (or a server error): the server may have taken
          // this draft all the same. What it holds is no longer known, so even
          // a note put back as it was is sent, not taken for saved.
          const status = statusOf(error)
          if (wasSent && (!status || status >= 500)) lastSavedKeyRef.current = null
          setSaveStatus('failed')
        }
      } finally {
        isSavingRef.current = false
      }
    }
    saveChainRef.current = saveChainRef.current.then(() => save(draftToSave))
    return saveChainRef.current
  }

  // Another note opened in the same editor (side panel): start afresh.
  const openedNoteIdRef = useRef(noteId)
  const loadedNoteIdRef = useRef<string | undefined>(undefined)
  useEffect(() => {
    if (noteId === openedNoteIdRef.current) return
    openedNoteIdRef.current = noteId
    idRef.current = noteId
    setSavedNoteId(noteId)
    lastSavedKeyRef.current = null
    stopSavingRef.current = false
    failedKeyRef.current = null
    retryCountRef.current = 0
    newNoteIdRef.current = null
    loadedNoteIdRef.current = undefined
    setSaveStatus('idle')
  }, [noteId])

  // Fill the editor from the note when it opens. It is fetched again on
  // opening and after each save: the editor follows it only while nothing typed
  // here waits to be saved (a copy kept from an earlier visit was out of date,
  // a save landed after the editor opened, the note changed on another device).
  // What is being typed is never replaced.
  useEffect(() => {
    if (!note) return
    const loadedContactId = (isMyNote ? note.contactAccountId : note.createdByAccountId) || null
    const loadedShared = isGhost || hidePerson ? false : note.isShared || false
    const storedKey = JSON.stringify({
      title: note.title || '',
      content: note.content || '',
      contactId: loadedContactId,
      isShared: !!loadedContactId && !isGhost && !hidePerson && loadedShared,
      isPinned: !!note.isPinned,
    })
    const isOpening = note.id !== loadedNoteIdRef.current
    const hasUnsavedChanges = isSavingRef.current || draftKey !== lastSavedKeyRef.current
    if (!isOpening && (hasUnsavedChanges || storedKey === lastSavedKeyRef.current)) return
    loadedNoteIdRef.current = note.id
    setTitle(note.title || '')
    setContent(note.content || '')
    setContactId(loadedContactId)
    setIsShared(loadedShared)
    setIsPinned(!!note.isPinned)
    // What is on screen is what is saved: opening a note doesn't save it.
    lastSavedKeyRef.current = storedKey
  }, [note, isMyNote])

  // `saveStatus` is among the dependencies so that the draft is looked at again
  // once a save is over: a change made meanwhile (the pin tapped twice, say), or
  // a save that failed and is to be tried again.
  useEffect(() => {
    if (!canEdit || isTranslated) return
    if (draftKey === lastSavedKeyRef.current) {
      // Back to what is saved after a save that failed (the pin tapped, then
      // tapped again): nothing waits any more, « Non enregistré » goes.
      if (saveStatus === 'failed') void persist(draft)
      return
    }
    // One save at a time: the draft is looked at again when this one is over.
    if (saveStatus === 'saving') return
    const isFailedDraft = saveStatus === 'failed' && draftKey === failedKeyRef.current
    // Refused by the server as it is: wait for a change in the note.
    if (isFailedDraft && !retryLaterRef.current) return
    const delay = isFailedDraft
      ? Math.min(RETRY_DELAY_MS * 2 ** retryCountRef.current, RETRY_DELAY_MAX_MS)
      : AUTOSAVE_DELAY_MS
    const timer = setTimeout(() => void persist(draft), delay)
    return () => clearTimeout(timer)
  }, [draftKey, canEdit, isTranslated, saveStatus])

  // Leaving the screen another way (swipe back, other menu): save the rest.
  const draftRef = useRef(draft)
  draftRef.current = draft
  const canSaveRef = useRef(canEdit && !isTranslated)
  canSaveRef.current = canEdit && !isTranslated
  useEffect(
    () => () => {
      if (canSaveRef.current) void persist(draftRef.current, { isLeaving: true })
    },
    [],
  )
  // The connection is back (the browser tells it): the save that failed is
  // tried at once, without waiting for its turn.
  useEffect(
    () =>
      onlineManager.subscribe((isOnline) => {
        if (isOnline && canSaveRef.current && failedKeyRef.current !== null) {
          void persist(draftRef.current)
        }
      }),
    [],
  )

  // On the phone the editor is a screen of its own, and the Android back
  // button or the iPhone back swipe closed it without a word, even when the
  // note could not be saved. While something waits to be saved, they now go
  // through the same path as the back arrow. Not on the web, where the browser
  // owns the back button, nor when the editor sits inside another screen.
  const guardsRemoval = Platform.OS !== 'web' && !onBack && !isCreationStack
  const [hasUnsavedNote, setHasUnsavedNote] = useState(false)
  // After every render (the refs it reads change without one), and after the
  // effect above that fills the editor from the note.
  useEffect(() => {
    if (!guardsRemoval) return
    setHasUnsavedNote(
      canSaveRef.current &&
        !stopSavingRef.current &&
        (isSavingRef.current || (!!draft.content.trim() && draftKey !== lastSavedKeyRef.current)),
    )
  })
  // Set once leaving is decided (saved, or « Quitter sans enregistrer »).
  const isLeavingRef = useRef(false)

  const handleBack = () => {
    if (onBack) {
      onBack()
    } else {
      isLeavingRef.current = true
      navigation.goBack()
    }
  }

  // The back arrow: save what is left, then leave. When that save fails (no
  // connection), the note is not dropped silently: the choice is asked.
  const [isLeaveModalVisible, setIsLeaveModalVisible] = useState(false)
  const handleClose = async () => {
    if (canEdit && !isTranslated) {
      await persist(draft)
      if (failedKeyRef.current !== null) {
        setIsLeaveModalVisible(true)
        return
      }
    }
    handleBack()
  }
  const isClosingFromSystemRef = useRef(false)
  const handleRemovalPrevented: Parameters<typeof usePreventRemove>[1] = ({ data }) => {
    // Our own way out, held back because the screen was not drawn again yet:
    // the same action sent again goes through. So does anything that is not a
    // « back » (Android button: GO_BACK, iPhone swipe: POP): signing out
    // resets the navigation, and holding that reset kept a signed-out user
    // inside the editor. The draft is still handed over when the screen goes.
    if (isLeavingRef.current || !SYSTEM_BACK_ACTIONS.includes(data.action.type)) {
      navigation.dispatch(data.action)
      return
    }
    // Back pressed again while the save is under way: one way out, not two
    // (the second one would close the screen underneath as well).
    if (isClosingFromSystemRef.current) return
    isClosingFromSystemRef.current = true
    void handleClose().finally(() => {
      isClosingFromSystemRef.current = false
    })
  }
  const retryThenLeave = () => {
    setIsLeaveModalVisible(false)
    void handleClose()
  }
  const leaveWithoutSaving = () => {
    setIsLeaveModalVisible(false)
    stopSavingRef.current = true
    handleBack()
  }

  // ----- Dictation ----------------------------------------------------------
  const { isRecording, startRecording, stopRecording, duration } = useVoiceRecorder()
  const { mutateAsync: transcribe, isPending: isTranscribing } = useFtVoiceTranscribe()
  const handleDictate = async () => {
    if (isTranscribing) return
    if (!isRecording) {
      const started = await startRecording()
      if (!started) {
        showErrorToast(t('voiceNote.micUnavailableTitle'), t('voiceNote.micUnavailableMessage'))
      }
      return
    }
    const audio = await stopRecording()
    try {
      const spoken = await transcribe(audio)
      if (spoken) setContent((prev) => (prev.trim() ? `${prev.trimEnd()}\n${spoken}` : spoken))
    } catch {
      // The mutation shows the error.
    }
  }

  const showDeleteModal = () => {
    setIsDeleteModalVisible(true)
  }

  const handleDeleteMessage = () => {
    stopSavingRef.current = true
    if (idRef.current) {
      deleteNote({ id: idRef.current })
    }
    setIsDeleteModalVisible(false)
    // Back to the list once the note is gone: on the phone the editor is its
    // own screen and used to stay open on the deleted note.
    if (onComplete) {
      onComplete()
    } else {
      handleBack()
    }
  }

  const openProfile = (tab?: 'note') => {
    if (!contactId) return
    const nav = navigation as any
    nav.navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: contactId, ...(tab && { tab }) },
    })
  }
  const navigateToProfile = () => openProfile()

  // Another person chosen in « À propos de »: the note goes back to « Moi seul »
  // rather than being shared with someone new without asking.
  const choosePerson = (person: Contact) => {
    if (person.id === contactId) return
    setContactId(person.id)
    setIsShared(false)
    setPersonChanged(true)
    setPickedGhostId(person.isGhost || person.scope === Scope.GHOST ? person.id : null)
    pickedScopeRef.current =
      person.scope === Scope.PRIVATE || person.scope === Scope.PROFESSIONAL
        ? person.scope
        : undefined
  }
  // A note about nobody again: it cannot stay shared, and it is filed as « Privé »
  // like a note written with no person.
  const removePerson = () => {
    setContactId(null)
    setIsShared(false)
    setPersonChanged(true)
    setPickedGhostId(null)
    pickedScopeRef.current = Scope.PRIVATE
  }

  // « Qui peut lire cette note ? »: me only, or me and the contact (a real
  // contact, not a ghost profile: it has no account to read it).
  const contactIsGhost = isGhost || !!contact?.isGhost || contact?.scope === Scope.GHOST
  const contactFirstName = contact?.fullName?.split(' ')[0] ?? ''
  const [editorWidth, setEditorWidth] = useState(lastEditorWidth)
  const onEditorLayout = ({ nativeEvent }: LayoutChangeEvent) => {
    const { width } = nativeEvent.layout
    // On the web a screen hidden under another one (the profile opened from the
    // note) measures 0: the editor keeps its last real width, and its layout.
    if (width <= 0) return
    setEditorWidth(width)
    // The side panels and the phone take one column whatever their width: only
    // an editor whose width decides its layout tells the next one.
    if (!isMobile && !isVisible && !isCreationStack) lastEditorWidth = width
  }
  const isNarrow = editorWidth > 0 && editorWidth < TWO_COLUMN_MIN_WIDTH
  const isCompact = isMobile || isVisible || isCreationStack || isNarrow

  // On the phone, while the keyboard is up, the text gets the screen: « Qui
  // peut lire » and the person step aside, and « OK » closes the keyboard. The
  // short delay keeps them from flashing when going from the title to the text.
  const [isWriting, setIsWriting] = useState(false)
  const blurTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const onInputFocus = () => {
    if (blurTimer.current) clearTimeout(blurTimer.current)
    setIsWriting(true)
  }
  const onInputBlur = () => {
    // Two blurs can come in a row (a closing sheet takes the focus back on the
    // web): only the last one counts, or a lost timer ends the writing mode
    // while the text has the focus.
    if (blurTimer.current) clearTimeout(blurTimer.current)
    blurTimer.current = setTimeout(() => setIsWriting(false), WRITING_END_DELAY_MS)
  }
  useEffect(
    () => () => {
      if (blurTimer.current) clearTimeout(blurTimer.current)
    },
    [],
  )
  // Android closes the keyboard on « back » but leaves the field focused: the
  // screen stayed in writing mode with no keyboard. Leaving the field ends it.
  useEffect(() => {
    if (Platform.OS !== 'android') return
    const subscription = Keyboard.addListener('keyboardDidHide', () => Keyboard.dismiss())
    return () => subscription.remove()
  }, [])
  const isFocusMode = isMobile && isWriting
  // Mobile web: the on-screen keyboard covers the bottom of the page.
  const keyboardOffset = useKeyboardOffset()

  // « Moi et … » waits for a real person to be chosen.
  const isSharingDisabled = !contactId || contactIsGhost || contactNotAddedBack
  const sharingHint = !contactId
    ? t('notesForm.chooseFirst')
    : contactIsGhost
      ? t('notesForm.ghostNoApp')
      : contactNotAddedBack
        ? t('notesForm.notAddedBack')
        : null

  const renderPrivacyOption = (shared: boolean) => {
    const isActive = isShared === shared
    const isDisabled = shared && isSharingDisabled
    const sharedLabel = contactId
      ? t('notesForm.meAnd', { name: contactFirstName })
      : isCompact
        ? t('notesForm.meAndSomeone')
        : t('notesForm.meAndThisPerson')
    return (
      <TouchableOpacity
        key={shared ? 'shared' : 'personal'}
        testID={shared ? 'note-visibility-shared' : 'note-visibility-personal'}
        onPress={() => setIsShared(shared)}
        disabled={isDisabled}
        activeOpacity={0.7}
        style={[
          isCompact ? styles.privacyPill : styles.privacyOption,
          isActive && styles.privacyActive,
          isDisabled && styles.privacyDisabled,
        ]}
      >
        <Icon
          name={shared ? 'user' : 'lock'}
          size={theme.iconSize.small}
          color={isActive ? theme.colors.primary : theme.colors.greyBlue}
        />
        {isCompact ? (
          <Text
            type={isActive ? 'bodySmallBold' : 'bodySmall'}
            color={isActive ? theme.colors.primary : theme.colors.greyBlue}
            numberOfLines={1}
          >
            {shared ? sharedLabel : t('notesForm.onlyMe')}
          </Text>
        ) : (
          <>
            <View style={commonStyles.flex}>
              <Text
                type="bodySmallBold"
                color={isActive ? theme.colors.primary : theme.colors.text}
              >
                {shared ? sharedLabel : t('notesForm.onlyMe')}
              </Text>
              <Text type="bodyExtraSmall" color={theme.colors.lightGrey}>
                {shared
                  ? (sharingHint ?? t('notesForm.meAndSubtitle', { name: contactFirstName }))
                  : t('notesForm.onlyMeSubtitle')}
              </Text>
            </View>
            <View style={[styles.radio, isActive && styles.radioActive]} />
          </>
        )}
      </TouchableOpacity>
    )
  }

  // On the phone and in the side panel: first thing under the header.
  const privacyChoice = canEdit && (
    <View style={styles.privacyCompact}>
      <Text type="bodySmallBold" color={theme.colors.greyBlue}>
        {t('notesForm.whoCanRead')}
      </Text>
      <View style={styles.privacyRow}>
        {renderPrivacyOption(false)}
        {renderPrivacyOption(true)}
      </View>
      {!!sharingHint && (
        <Text testID="note-sharing-hint" type="bodyExtraSmall" color={theme.colors.greyBlue}>
          {sharingHint}
        </Text>
      )}
    </View>
  )

  const hasNote = !!(noteId || savedNoteId)
  const createdAt = dayjs(note?.createdAt)
  const dateLabel = hasNote
    ? t('notesForm.writtenOn', { date: createdAt.format('D MMM YYYY') })
    : t('notesForm.todayDate', { date: dayjs().format('D MMM YYYY') })
  // Next to the tools on the phone: « Aujourd'hui », « 28 sept. » or « 28 sept. 2025 ».
  const shortDate =
    !hasNote || createdAt.isSame(dayjs(), 'day')
      ? t('notesForm.today')
      : createdAt.format(createdAt.isSame(dayjs(), 'year') ? 'D MMM' : 'D MMM YYYY')
  const writtenOn = hasNote ? createdAt.format('D MMM YYYY') : null

  // The one outlined button of the editor: « Dicter ».
  const dictateButton = (
    <TouchableOpacity
      testID="note-dictate"
      style={[styles.dictateButton, isRecording && styles.dictateButtonRecording]}
      onPress={() => void handleDictate()}
      disabled={isTranscribing || isTranslated}
    >
      {isTranscribing ? (
        <ActivityIndicator size="small" color={theme.colors.primary} />
      ) : (
        <Icon
          name="microphone"
          size={theme.iconSize.small}
          color={isRecording ? theme.colors.error : theme.colors.primary}
        />
      )}
      <Text type="bodySmallBold" color={isRecording ? theme.colors.error : theme.colors.primary}>
        {isRecording
          ? t('notes.stopRecording', { time: formatDuration(duration) })
          : t('notesForm.dictate')}
      </Text>
    </TouchableOpacity>
  )

  // The button says the state (« Épinglée », highlighted) rather than the
  // action (« Désépingler »): one look tells the note is pinned.
  const pinButton = (
    <TouchableOpacity
      testID="note-pin"
      style={[styles.pinAction, isPinned && styles.pinActionActive]}
      onPress={() => setIsPinned(!isPinned)}
    >
      <Icon name="pin" size={theme.iconSize.small} color={theme.colors.primary} />
      <Text type={isPinned ? 'bodySmallBold' : 'bodySmall'} color={theme.colors.primary}>
        {isPinned ? t('notesForm.pinned') : t('notesForm.pin')}
      </Text>
    </TouchableOpacity>
  )

  // « À propos de » at the bottom of the phone and of the side panel. On the
  // phone the chosen person takes one line (a tap opens the details); with no
  // person yet, a field opens the list of profiles.
  const phonePerson = contactId ? (
    <NotePersonCompact
      contactId={contactId}
      currentNoteId={savedNoteId}
      writtenOn={writtenOn}
      canChange={canEdit}
      onOpenProfile={navigateToProfile}
      onOpenNotes={() => openProfile('note')}
      // Left a moment for the details sheet to close first.
      onChangePerson={() => setTimeout(() => setIsChooserOpen(true), SHEET_SWAP_DELAY_MS)}
    />
  ) : (
    <View>
      <Text type="bodySmallBold" color={theme.colors.greyBlue}>
        {t('notesForm.about')}
      </Text>
      <TouchableOpacity
        testID="note-choose-person"
        style={styles.chooseField}
        onPress={() => setIsChooserOpen(true)}
      >
        <Icon name="userPlus" size={20} color={theme.colors.primary} />
        <Text type="body" color={theme.colors.primary} style={commonStyles.flex}>
          {t('notesForm.choosePerson')}
        </Text>
        <Icon name="arrowDown" size={18} color={theme.colors.primary} />
      </TouchableOpacity>
    </View>
  )
  const compactPerson = isMobile ? (
    phonePerson
  ) : (
    <NoteAbout
      contactId={contactId}
      currentNoteId={savedNoteId}
      canChange={canEdit}
      placement="above"
      onSelect={choosePerson}
      onRemove={removePerson}
      onOpenProfile={navigateToProfile}
      onOpenNotes={() => openProfile('note')}
    />
  )

  // Under the title: where the pinned note shows up. A personal note on someone
  // goes to « À retenir » on their profile (a tap opens it); every pinned note
  // goes to the top of the notes list.
  const isInToRemember = !!contactId && !isShared && !hidePerson
  const pinnedBadge = isPinned && (
    <TouchableOpacity
      testID="note-pinned-badge"
      style={styles.pinnedBadge}
      onPress={() => openProfile('note')}
      disabled={!isInToRemember}
    >
      <Icon name="pin" size={theme.iconSize.extraSmall} color={theme.colors.primary} />
      <Text type="bodySmall" color={theme.colors.primary}>
        {isInToRemember
          ? t('notesForm.pinnedWhere', { name: contactFirstName })
          : t('notesForm.pinnedTop')}
      </Text>
    </TouchableOpacity>
  )

  const editor = (
    <>
      {/* Header */}
      <View style={[styles.headerContainer, isWeb && { paddingTop: theme.spacing.md }]}>
        {!isVisible && (
          <Icon
            name="arrowBack"
            size={25}
            color={theme.colors.text}
            onPress={() => void handleClose()}
            testID="note-back"
          />
        )}

        <View style={styles.userInfoContainer}>
          <Text type="titleBold">{!noteId ? t('notesForm.addNote') : t('notesForm.editNote')}</Text>
        </View>

        <View style={styles.headerIconsContainer}>
          {/* On the computer « Enregistrement automatique » says from the start
              that there is nothing to save by hand. */}
          {canEdit && (saveStatus !== 'idle' || !isCompact) && (
            // « Non enregistré »: a tap tries again at once.
            <TouchableOpacity
              testID="note-save-status"
              style={styles.saveStatus}
              disabled={saveStatus !== 'failed'}
              onPress={() => void persist(draft)}
            >
              {saveStatus === 'saved' && (
                <Icon name="check" size={theme.iconSize.extraSmall} color={theme.colors.green} />
              )}
              <Text
                type={saveStatus === 'failed' ? 'bodySmallBold' : 'bodySmall'}
                color={
                  saveStatus === 'saved'
                    ? theme.colors.green
                    : saveStatus === 'failed'
                      ? theme.colors.error
                      : theme.colors.greyBlue
                }
              >
                {saveStatus === 'saved'
                  ? t('editNote.saved')
                  : saveStatus === 'saving'
                    ? t('editNote.saving')
                    : saveStatus === 'failed'
                      ? t('editNote.notSaved')
                      : t('notesForm.autoSave')}
              </Text>
            </TouchableOpacity>
          )}
          <View ref={translateIconRef} style={styles.iconButton} collapsable={false}>
            {isTranslating ? (
              <ActivityIndicator size="small" color={theme.colors.primary} />
            ) : (
              <Icon
                name="translate"
                size={theme.iconSize.medium}
                color={isTranslated ? theme.colors.primary : theme.colors.text}
                onPress={() => {
                  // Measure the icon in screen coords so the picker can
                  // anchor as a popover just below it (right-aligned).
                  translateIconRef.current?.measureInWindow((x, y, w, h) => {
                    const PICKER_WIDTH = 200
                    setPickerPos({
                      x: Math.max(8, x + w - PICKER_WIDTH),
                      y: y + h + 4,
                    })
                    setShowLangPicker(true)
                  })
                }}
              />
            )}
          </View>
          {hasNote && isMyNote && (
            <View style={styles.iconButton}>
              <Icon
                name="trash"
                size={theme.iconSize.small}
                color={theme.colors.error}
                onPress={showDeleteModal}
                testID="note-delete"
              />
            </View>
          )}
        </View>
      </View>

      <TranslateLangPicker
        isVisible={showLangPicker}
        setIsVisible={setShowLangPicker}
        currentLang={translatedTo}
        onPick={onPickLang}
        onRevert={() => setTranslatedTo(null)}
        position={pickerPos}
      />
      <View
        style={[
          commonStyles.flex,
          !isMobile && commonStyles.row,
          !isMobile && commonStyles.alignStart,
        ]}
      >
        <View style={[commonStyles.flex, commonStyles.fullHeight]}>
          {/* On the phone and in the side panel, « Qui peut lire » comes first. */}
          {isCompact && !isFocusMode && !hidePerson && privacyChoice}
          <TextInput
            testID="note-title-input"
            value={displayedTitle}
            onChangeText={setTitle}
            placeholder={t('notesForm.titlePlaceholder')}
            style={[{ fontSize: theme.fontSize.jumbo }, styles.titleText]}
            noBorder
            // On the phone the keyboard opens by itself only for a new note
            // on someone already chosen (from their profile). An existing note
            // opens for reading, and a new one first shows « À propos de ».
            autoFocus={!isReadOnly && (isMobile ? !noteId && !!contactId : true)}
            editable={!isTranslated && !isReadOnly && !isLoadingNote}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
          {pinnedBadge}
          <TextInput
            testID="note-content-input"
            value={displayedContent}
            onChangeText={setContent}
            placeholder={t('notesForm.notePlaceholder')}
            contentStyle={commonStyles.fullHeight}
            containerStyle={commonStyles.flex}
            style={[commonStyles.fullHeight, styles.contentText]}
            isMultiline
            noBorder
            noPaddingTop
            editable={!isTranslated && !isReadOnly && !isLoadingNote}
            onFocus={onInputFocus}
            onBlur={onInputBlur}
          />
          <View
            style={[
              styles.bottomContainer,
              isCompact ? styles.bottomContainerCompact : styles.bottomContainerDesktop,
            ]}
          >
            {/* On the computer the person and the date are in the panel on the right. */}
            {isCompact && canEdit && !isFocusMode && !hidePerson && compactPerson}
            {canEdit && (
              <View style={styles.tools}>
                {dictateButton}
                {pinButton}
                {isCompact &&
                  (isFocusMode ? (
                    <TouchableOpacity
                      testID="note-keyboard-done"
                      style={styles.toolsEnd}
                      onPress={Keyboard.dismiss}
                    >
                      <Text type="bodyBold" color={theme.colors.primary}>
                        {t('notesForm.done')}
                      </Text>
                    </TouchableOpacity>
                  ) : (
                    <View testID="note-date-short" style={styles.toolsEnd}>
                      <Icon name="calendar" size={15} color={theme.colors.greyBlue} />
                      <Text type="bodySmall" color={theme.colors.greyBlue}>
                        {shortDate}
                      </Text>
                    </View>
                  ))}
              </View>
            )}
            {/* A note someone shared with me: its date and who shared it. */}
            {isReadOnly && (
              <>
                <View style={styles.dateContainer}>
                  <Icon name="calendar" size={theme.iconSize.extraSmall} />
                  <Text type="bodySmall">{createdAt.format('D MMM YYYY')}</Text>
                </View>
                <TouchableOpacity style={styles.sharedContainer} onPress={navigateToProfile}>
                  <Image
                    source={contact?.avatarUrl ? { uri: contact.avatarUrl } : defaultImage}
                    style={styles.avatar}
                  />
                  <Text type="bodySmallBold">
                    {t('notes.sharedBy', { name: contact?.fullName ?? '' })}
                  </Text>
                </TouchableOpacity>
              </>
            )}
          </View>
        </View>
        {/* On the computer: who the note is about, who can read it, its date. */}
        {isMyNote && !isCompact && (
          <View style={styles.rightContainer}>
            <NoteAbout
              contactId={contactId}
              currentNoteId={savedNoteId}
              canChange={canEdit}
              onSelect={choosePerson}
              onRemove={removePerson}
              onOpenProfile={navigateToProfile}
              onOpenNotes={() => openProfile('note')}
            />
            {canEdit && (
              <View style={styles.panelSection}>
                <Text type="bodySmallBold" color={theme.colors.greyBlue}>
                  {t('notesForm.whoCanRead')}
                </Text>
                <View style={styles.privacyColumn}>
                  {renderPrivacyOption(false)}
                  {renderPrivacyOption(true)}
                </View>
              </View>
            )}
            <View style={styles.panelSection}>
              <Text type="bodySmallBold" color={theme.colors.greyBlue}>
                {t('notesForm.date')}
              </Text>
              <View testID="note-date" style={styles.panelDate}>
                <Icon name="calendar" size={16} color={theme.colors.greyBlue} />
                <Text type="bodySmall" color={theme.colors.greyBlue}>
                  {dateLabel}
                </Text>
              </View>
            </View>
          </View>
        )}
      </View>
      {/* No « Fermer » any more: the note saves itself and the arrow closes it. */}
      {isReadOnly && (
        <View style={styles.footer}>
          <Text type="bodySmall" color={theme.colors.greyBlue}>
            {t('notes.readOnly')}
          </Text>
        </View>
      )}
    </>
  )

  return (
    <SafeAreaView
      style={[
        styles.container,
        isMobile && keyboardOffset > 0 && { paddingBottom: keyboardOffset },
      ]}
      onLayout={onEditorLayout}
    >
      {/* On the phone the tools ride above the keyboard, as in the conversations.
          No `offsetTopInset` here: the SafeAreaView is the direct parent, so the
          measured frame already starts under the status bar. With it, the iPhone
          left a blank band as high as the status bar above the keyboard. */}
      {isMobile ? <KeyboardAwareView isFullScreen>{editor}</KeyboardAwareView> : editor}
      {guardsRemoval && (
        <NoteRemovalGuard isActive={hasUnsavedNote} onPrevented={handleRemovalPrevented} />
      )}
      <DeleteModal
        isVisible={isDeleteModalVisible}
        setIsVisible={setIsDeleteModalVisible}
        title={t('editNote.deleteConfirm')}
        onDelete={handleDeleteMessage}
      />
      {/* Closing a note that could not be saved: try again, or leave it. */}
      <Modal
        visible={isLeaveModalVisible}
        transparent
        animationType="fade"
        onRequestClose={() => setIsLeaveModalVisible(false)}
      >
        <View style={styles.leaveOverlay}>
          <View testID="note-unsaved-modal" style={styles.leaveCard}>
            <Text type="subtitleBold">{t('editNote.notSavedTitle')}</Text>
            <Text type="bodySmall" color={theme.colors.greyBlue}>
              {t('editNote.notSavedMessage')}
            </Text>
            <View style={styles.leaveActions}>
              <TouchableOpacity
                testID="note-unsaved-retry"
                style={styles.leaveRetry}
                onPress={retryThenLeave}
              >
                <Text type="bodyBold" color={theme.colors.primary}>
                  {t('editNote.retry')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity testID="note-unsaved-leave" onPress={leaveWithoutSaving}>
                <Text type="body" color={theme.colors.error}>
                  {t('editNote.leaveWithoutSaving')}
                </Text>
              </TouchableOpacity>
              <TouchableOpacity
                testID="note-unsaved-stay"
                onPress={() => setIsLeaveModalVisible(false)}
              >
                <Text type="body" color={theme.colors.greyBlue}>
                  {t('editNote.keepEditing')}
                </Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
      {/* On the phone the list of profiles opens in a sheet from the bottom. */}
      {isMobile && (
        <BottomModalBase
          isVisible={isChooserOpen}
          setIsVisible={setIsChooserOpen}
          largePaddingTop={false}
          maxHeightRatio={CHOOSER_HEIGHT_RATIO}
        >
          <View style={styles.chooserSheet}>
            <Text type="subtitleBold">{t('notesForm.choosePerson')}</Text>
            <NotePersonPicker
              variant="sheet"
              selectedContactId={contactId}
              onSelect={(person) => {
                setIsChooserOpen(false)
                choosePerson(person)
              }}
              onRemove={() => {
                setIsChooserOpen(false)
                removePerson()
              }}
            />
          </View>
        </BottomModalBase>
      )}
    </SafeAreaView>
  )
}

const getStyle = (theme: Theme) =>
  StyleSheet.create({
    container: {
      flex: 1,
      backgroundColor: theme.colors.background,
    },
    headerContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: theme.spacing.md,
      paddingBottom: theme.spacing.md,
      backgroundColor: theme.colors.white,
      borderBottomWidth: 0.5,
      borderBottomColor: theme.colors.lightGrey,
    },
    userInfoContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      flex: 1,
      marginLeft: theme.spacing.sm,
    },
    avatar: {
      width: 20,
      height: 20,
      borderRadius: 10,
      marginRight: theme.spacing.sm,
    },
    headerIconsContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    saveStatus: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.xs,
      marginRight: theme.spacing.sm,
    },
    iconButton: {
      padding: theme.spacing.xs,
    },
    // The note reads like the notes list: bold title, dark text.
    titleText: {
      color: theme.colors.black,
      fontWeight: 'bold',
      // On the web a text field keeps a width of its own (about twenty
      // letters): in a narrower column the title stuck out of it.
      minWidth: 0,
    },
    contentText: {
      color: theme.colors.text,
      fontSize: theme.fontSize.medium,
      lineHeight: 26,
    },
    bottomContainer: {
      paddingHorizontal: theme.spacing.lg,
      gap: 10,
    },
    bottomContainerDesktop: {
      paddingBottom: theme.spacing.xxl,
    },
    // Phone and side panel: the person, then the tools, under a thin line.
    bottomContainerCompact: {
      paddingTop: 10,
      paddingBottom: theme.spacing.md,
      borderTopWidth: 1,
      borderTopColor: theme.colors.grey20,
    },
    tools: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      gap: 10,
    },
    // The date, or « OK » while writing, at the end of the tools.
    toolsEnd: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 5,
      marginLeft: 'auto',
    },
    chooseField: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 10,
      height: 52,
      marginTop: theme.spacing.sm,
      paddingHorizontal: 14,
      borderWidth: 1,
      borderStyle: 'dashed',
      borderColor: theme.colors.primary,
      borderRadius: theme.border.large,
    },
    chooserSheet: {
      flex: 1,
      width: '100%',
      gap: theme.spacing.md,
      paddingBottom: theme.spacing.lg,
    },
    // The one outlined button of the screen: white, 2 px blue ring.
    dictateButton: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      height: 38,
      paddingHorizontal: 18,
      borderRadius: theme.border.jumbo,
      borderWidth: 2,
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.white,
    },
    dictateButtonRecording: {
      borderColor: theme.colors.error,
    },
    // Same size pinned or not, so nothing moves when it is pressed.
    pinAction: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      paddingVertical: 6,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.border.jumbo,
    },
    pinActionActive: {
      backgroundColor: theme.colors.primary + '14',
    },
    pinnedBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: theme.spacing.xs,
      marginLeft: theme.spacing.lg,
      marginBottom: theme.spacing.sm,
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: 10,
      borderRadius: theme.border.jumbo,
      backgroundColor: theme.colors.primary + '12',
    },
    dateContainer: {
      flexDirection: 'row',
      alignItems: 'center',
      alignSelf: 'flex-start',
      gap: 5,
      padding: 5,
      borderRadius: 5,
      borderColor: theme.colors.grey10,
      borderWidth: 1,
    },
    sharedContainer: {
      flexDirection: 'row',
      alignItems: 'center',
    },
    footer: {
      padding: 16,
      backgroundColor: theme.colors.white,
    },
    leaveOverlay: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.lg,
      backgroundColor: 'rgba(0, 0, 0, 0.4)',
    },
    leaveCard: {
      width: '100%',
      maxWidth: 380,
      gap: theme.spacing.sm,
      padding: theme.spacing.xl,
      borderRadius: theme.border.large,
      backgroundColor: theme.colors.white,
    },
    leaveActions: {
      alignItems: 'center',
      gap: theme.spacing.md,
      marginTop: theme.spacing.md,
    },
    // The one outlined button of the dialog: white, 2 px blue ring.
    leaveRetry: {
      alignSelf: 'stretch',
      alignItems: 'center',
      justifyContent: 'center',
      height: 44,
      borderRadius: theme.border.jumbo,
      borderWidth: 2,
      borderColor: theme.colors.primary,
    },
    rightContainer: {
      height: '100%',
      width: 360,
      paddingTop: 18,
      paddingHorizontal: theme.spacing.xxl,
      borderLeftWidth: 1,
      borderLeftColor: theme.colors.grey20,
    },
    panelSection: {
      marginTop: theme.spacing.xxl,
    },
    panelDate: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 6,
      marginTop: 10,
    },
    privacyCompact: {
      paddingHorizontal: theme.spacing.lg,
      paddingTop: theme.spacing.md,
      paddingBottom: theme.spacing.xs,
      gap: theme.spacing.xs,
    },
    privacyColumn: {
      gap: theme.spacing.sm,
      marginTop: theme.spacing.xs,
    },
    privacyRow: {
      flexDirection: 'row',
      gap: theme.spacing.sm,
    },
    privacyOption: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: theme.spacing.sm,
      borderWidth: 1,
      borderColor: theme.colors.grey20,
      borderRadius: theme.border.large,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
    },
    privacyPill: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'center',
      gap: theme.spacing.xs,
      minHeight: 34,
      borderWidth: 1,
      borderColor: theme.colors.grey20,
      borderRadius: theme.border.jumbo,
      paddingHorizontal: theme.spacing.sm,
    },
    privacyActive: {
      borderColor: theme.colors.primary,
      backgroundColor: theme.colors.primary + '0F',
    },
    privacyDisabled: {
      opacity: 0.45,
    },
    radio: {
      width: 18,
      height: 18,
      borderRadius: 9,
      borderWidth: 1.5,
      borderColor: theme.colors.lightGrey,
    },
    radioActive: {
      borderWidth: 5,
      borderColor: theme.colors.primary,
    },
  })
